import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEmails } from '../api';

export default function EmailsPage() {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');

  const fetchEmails = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const params = { limit: '50', offset: String(offset) };
      if (status) params.status = status;
      if (category) params.category = category;
      if (search) params.search = search;

      const data = await getEmails(params);
      setEmails(data.emails);
      setPagination(data.pagination);
    } catch {
      // handled
    } finally {
      setLoading(false);
    }
  }, [status, category, search]);

  useEffect(() => {
    fetchEmails(0);
  }, [fetchEmails]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEmails(0);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Email Inbox</h1>
        <p>Emails received at support@, legal@, and privacy@naturalseed.app</p>
      </div>

      <form className="toolbar" onSubmit={handleSearch}>
        <input
          className="search-input"
          placeholder="Search emails..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
          <option value="archived">Archived</option>
        </select>
        <select className="filter-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          <option value="support">Support</option>
          <option value="legal">Legal</option>
          <option value="privacy">Privacy</option>
          <option value="general">General</option>
        </select>
      </form>

      {loading ? (
        <div className="loading">Loading emails...</div>
      ) : emails.length === 0 ? (
        <div className="empty-state"><p>No emails found.</p></div>
      ) : (
        <>
          <div className="card">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>From</th>
                    <th>Subject</th>
                    <th>To</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((email) => (
                    <tr
                      key={email.id}
                      className="clickable"
                      onClick={() => navigate(`/emails/${email.id}`)}
                      style={{ fontWeight: email.status === 'unread' ? 600 : 400 }}
                    >
                      <td>
                        {email.status === 'unread' && <span className="dot-unread" />}
                      </td>
                      <td>
                        {email.fromName || email.fromAddress}
                        {email.fromName && (
                          <span style={{ color: '#888', fontSize: 12, marginLeft: 6 }}>
                            {email.fromAddress}
                          </span>
                        )}
                      </td>
                      <td>{email.subject || '(No subject)'}</td>
                      <td>
                        <span className={`badge badge-${categoryBadge(email.category)}`}>
                          {email.category}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${statusBadge(email.status)}`}>
                          {email.status}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 13, color: '#888' }}>
                        {formatDate(email.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pagination">
            <span>
              Showing {pagination.offset + 1}–{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.offset === 0}
                onClick={() => fetchEmails(Math.max(0, pagination.offset - pagination.limit))}
              >
                Previous
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={!pagination.hasMore}
                onClick={() => fetchEmails(pagination.offset + pagination.limit)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

function categoryBadge(cat) {
  switch (cat) {
    case 'support': return 'blue';
    case 'legal': return 'purple';
    case 'privacy': return 'yellow';
    default: return 'gray';
  }
}

function statusBadge(status) {
  switch (status) {
    case 'unread': return 'blue';
    case 'read': return 'gray';
    case 'replied': return 'green';
    case 'archived': return 'gray';
    default: return 'gray';
  }
}
